// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import ReactDOM from 'react-dom';
import { withRouter, RouteComponentProps } from 'react-router';

import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Checkbox from 'antd/lib/checkbox';
import Collapse from 'antd/lib/collapse';

import CVATTooltip from 'components/common/cvat-tooltip';
import Menu, { MenuInfo } from 'components/dropdown-menu';

import { getCore, JobStage } from 'cvat-core-wrapper';
import { t } from 'utils/i18n';
import AnnotationsActionsModalContent from '../annotations-actions/annotations-actions-modal';
import { openAddImagesModal } from '../workspace/add-images-modal';

const core = getCore();

interface Props {
    taskMode: string;
    jobInstance: any;
    onClickMenu(params: MenuInfo): void;
    stopFrame: number;
    removeAnnotations(startnumber: number | null, endnumber: number | null, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(afterSave?: () => void): void;
}

export enum Actions {
    LOAD_JOB_ANNO = 'load_job_anno',
    LOAD_JOB_REFERENCE = 'load_job_reference',
    EXPORT_JOB_DATASET = 'export_job_dataset',
    EXPORT_JOB_BLENDER = 'export_job_blender',
    REMOVE_ANNOTATIONS = 'remove_annotations',
    RUN_ACTIONS = 'run_actions',
    OPEN_TASK = 'open_task',
    FINISH_JOB = 'finish_job',
    RENEW_JOB = 'renew_job',
}

function AnnotationMenuComponent(props: Props & RouteComponentProps): JSX.Element {
    const {
        jobInstance,
        stopFrame,
        history,
        onClickMenu,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
    } = props;

    const jobStage = jobInstance.stage;
    const jobState = jobInstance.state;
    const taskID = jobInstance.taskId;
    const { JobState } = core.enums;

    function onClickMenuWrapper(params: MenuInfo): void {
        function checkUnsavedChanges(_params: MenuInfo): void {
            if (jobInstance.annotations.hasUnsavedChanges()) {
                Modal.confirm({
                    title: t('The job has unsaved annotations'),
                    content: t('Would you like to save changes before continue?'),
                    className: 'cvat-modal-content-save-job',
                    okButtonProps: {
                        children: t('Save'),
                    },
                    cancelButtonProps: {
                        children: 'No',
                    },
                    onOk: () => {
                        saveAnnotations(() => onClickMenu(_params));
                    },
                    onCancel: () => {
                        // do not ask leave confirmation
                        setForceExitAnnotationFlag(true);
                        setTimeout(() => {
                            onClickMenu(_params);
                        });
                    },
                });
            } else {
                onClickMenu(_params);
            }
        }

        if (params.key === Actions.REMOVE_ANNOTATIONS) {
            let removeFrom: number | null;
            let removeUpTo: number | null;
            let removeOnlyKeyframes = false;
            const { Panel } = Collapse;
            Modal.confirm({
                title: t('Remove Annotations'),
                content: (
                    <div>
                        <Text>{`${t('You are going to remove the annotations from the client.')} `}</Text>
                        <Text>{t('It will stay on the server till you save the job. Continue?')}</Text>
                        <br />
                        <br />
                        <Collapse bordered={false}>
                            <Panel header={<Text>{t('Select Range')}</Text>} key={1}>
                                <Text>{`${t('From:')} `}</Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => {
                                        removeFrom = value;
                                    }}
                                />
                                <Text>{`  ${t('To:')} `}</Text>
                                <InputNumber
                                    min={0}
                                    max={stopFrame}
                                    onChange={(value) => {
                                        removeUpTo = value;
                                    }}
                                />
                                <CVATTooltip title={t('Applicable only for annotations in range')}>
                                    <br />
                                    <br />
                                    <Checkbox
                                        onChange={(check) => {
                                            removeOnlyKeyframes = check.target.checked;
                                        }}
                                    >
                                        {t('Delete only keyframes for tracks')}
                                    </Checkbox>
                                </CVATTooltip>
                            </Panel>
                        </Collapse>
                    </div>
                ),
                className: 'cvat-modal-confirm-remove-annotation',
                onOk: () => {
                    removeAnnotations(removeFrom, removeUpTo, removeOnlyKeyframes);
                },
                okButtonProps: {
                    type: 'primary',
                    danger: true,
                },
                okText: t('Delete'),
            });
        } else if (params.key === Actions.LOAD_JOB_REFERENCE) {
            openAddImagesModal(`/tasks/${taskID}/data`);
        } else if (params.key.startsWith('state:')) {
            Modal.confirm({
                title: t('Do you want to change current job state?'),
                content: t(
                    'Job state will be switched to “{state}”. Continue?',
                    { state: t(params.key.split(':')[1]) },
                ),
                okText: t('Continue'),
                cancelText: t('Cancel'),
                className: 'cvat-modal-content-change-job-state',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.FINISH_JOB) {
            Modal.confirm({
                title: t('The job stage is going to be switched'),
                content: t('Stage will be changed to "acceptance". Would you like to continue?'),
                okText: t('Continue'),
                cancelText: t('Cancel'),
                className: 'cvat-modal-content-finish-job',
                onOk: () => {
                    checkUnsavedChanges(params);
                },
            });
        } else if (params.key === Actions.RENEW_JOB) {
            Modal.confirm({
                title: t('Do you want to renew the job?'),
                content: t('Stage will be set to "in progress", state will be set to "annotation". Would you like to continue?'),
                okText: t('Continue'),
                cancelText: t('Cancel'),
                className: 'cvat-modal-content-renew-job',
                onOk: () => {
                    onClickMenu(params);
                },
            });
        } else {
            onClickMenu(params);
        }
    }

    const computeClassName = (menuItemState: string): string => {
        if (menuItemState === jobState) return 'cvat-submenu-current-job-state-item';
        return '';
    };

    return (
        <Menu
            triggerSubMenuAction='click'
            onClick={(params: MenuInfo) => {
                onClickMenuWrapper(params);
            }}
            className='cvat-annotation-menu'
            selectable={false}
        >
            <Menu.Item key={Actions.LOAD_JOB_ANNO}>{t('Upload annotations')}</Menu.Item>
            <Menu.Item key={Actions.LOAD_JOB_REFERENCE}>{t('Add images')}</Menu.Item>
            <Menu.Item key={Actions.EXPORT_JOB_DATASET}>{t('Export job dataset')}</Menu.Item>
            <Menu.Item key={Actions.EXPORT_JOB_BLENDER}>{t('Export Blender files')}</Menu.Item>
            <Menu.Item key={Actions.REMOVE_ANNOTATIONS}>{t('Remove annotations')}</Menu.Item>
            <Menu.Item
                key={Actions.RUN_ACTIONS}
                onClick={() => {
                    const div = window.document.createElement('div');
                    window.document.body.append(div);
                    ReactDOM.render(
                        <AnnotationsActionsModalContent onClose={() => div.remove()} />, div,
                    );
                }}
            >
                {t('Run actions')}
            </Menu.Item>
            <Menu.Item key={Actions.OPEN_TASK}>
                <a
                    href={`/tasks/${taskID}`}
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        history.push(`/tasks/${taskID}`);
                        return false;
                    }}
                >
                    {t('Open the task')}
                </a>
            </Menu.Item>
            <Menu.SubMenu popupClassName='cvat-annotation-menu-job-state-submenu' key='job-state-submenu' title={t('Change job state')}>
                <Menu.Item key={`state:${JobState.NEW}`}>
                    <Text className={computeClassName(JobState.NEW)}>{t(JobState.NEW)}</Text>
                </Menu.Item>
                <Menu.Item key={`state:${JobState.IN_PROGRESS}`}>
                    <Text className={computeClassName(JobState.IN_PROGRESS)}>{t(JobState.IN_PROGRESS)}</Text>
                </Menu.Item>
                <Menu.Item key={`state:${JobState.REJECTED}`}>
                    <Text className={computeClassName(JobState.REJECTED)}>{t(JobState.REJECTED)}</Text>
                </Menu.Item>
                <Menu.Item key={`state:${JobState.COMPLETED}`}>
                    <Text className={computeClassName(JobState.COMPLETED)}>{t(JobState.COMPLETED)}</Text>
                </Menu.Item>
            </Menu.SubMenu>
            {[JobStage.ANNOTATION, JobStage.VALIDATION].includes(jobStage) ?
                <Menu.Item key={Actions.FINISH_JOB}>{t('Finish the job')}</Menu.Item> : null}
            {jobStage === JobStage.ACCEPTANCE ?
                <Menu.Item key={Actions.RENEW_JOB}>{t('Renew the job')}</Menu.Item> : null}
        </Menu>
    );
}

export default withRouter(AnnotationMenuComponent);
